import styled from "@emotion/styled";
import { Box, useTheme } from "@kaltura/mediaspace-shared-styled";
import ChannelsList from "./channels-list/ChannelsList";
import { translate, useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import { Link, Typography } from "@kaltura/ds-react-components";
import { useMemo } from "react";
import { GlobalSearchChannelResultsResponse } from "@kaltura/mediaspace-shared-types";

export interface ChannelSearchResultsProps extends GlobalSearchChannelResultsResponse {
    /**
     * wide / narrow space, how much space is reserved for the component
     */
    isWideLayout: boolean;
}

const StyledChannelSearchResults = styled(Box)(({ theme }) => ({
    width: "100%",
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(5),
}));

const HeadingWrapper = styled("div")(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    alignContent: "center",
    alignItems: "flex-end",
    marginBottom: theme.spacing(3),
}));

const baseClassName = "kms-ds-global-search-channel-results";

export function ChannelSearchResults({
    eSearchCategoryResponse,
    isWideLayout,
    allChannelsResultsUrl,
}: ChannelSearchResultsProps) {
    const categories = eSearchCategoryResponse.objects;

    const theme = useTheme();
    const isSmScreen = useMediaQuery(theme.breakpoints.up(theme.breakpoints.values.sm));
    const isMdScreen = useMediaQuery(theme.breakpoints.up(theme.breakpoints.values.md));
    const isLgScreen = useMediaQuery(theme.breakpoints.up(theme.breakpoints.values.lg));
    const isXlScreen = useMediaQuery(theme.breakpoints.up(theme.breakpoints.values.xl));

    const numberOfItemsToShow = useMemo(() => {
        if (isXlScreen) {
            return isWideLayout ? 8 : 6;
        }
        if (isLgScreen) {
            return isWideLayout ? 7 : 5;
        }
        if (isMdScreen) {
            return isWideLayout ? 6 : 5;
        }
        if (isSmScreen) {
            return 4;
        }
        return 3;
    }, [isSmScreen, isMdScreen, isLgScreen, isXlScreen, isWideLayout]);

    return (
        <StyledChannelSearchResults className={`${baseClassName}`}>
            <HeadingWrapper>
                <Typography component={"h2"} variant={"h5"} className={`${baseClassName}-heading`}>
                    {translate("Channel results")}
                </Typography>
                <Link
                    href={allChannelsResultsUrl}
                    size={"small"}
                    className={`${baseClassName}-link`}
                    aria-label={translate("See all channel results")}
                >
                    {translate("See all")}
                </Link>
            </HeadingWrapper>
            <ChannelsList channels={categories} numberOfItemsToShow={numberOfItemsToShow} />
        </StyledChannelSearchResults>
    );
}

export default ChannelSearchResults;
